/// <amd-module name="Wit/WorkItemModel/OM/WorkItemConstants" />

export namespace ExtensionConstants {
    export const LayoutUserSettingsProvider = "ms.vss-work-web.work-item-layout-user-settings-data-provider";
    export const UnfollowsDataProvider = "ms.vss-work-web.work-item-unfollows-data-provider";
}

export enum ServerDefaultValueType {
    None = 0,
    ServerDateTime = 1,
    CallerIdentity = 2,
    RandomGuid = 3
}

export const RegisteredLinkTypeNames = {
    Related: "Related Workitem",
    Hyperlink: "Workitem Hyperlink",
    Changeset: "Fixed in Changeset",
    VersionedItem: "Source Code File",
    Commit: "Fixed in Commit",
    Branch: "Branch",
    Tag: "Tag",
    PullRequest: "Pull Request",
    ResultAttachment: "Result Attachment",
    TestResult: "Test Result",
    Test: "Test",
    Storyboard: "Storyboard",
    Build: "Build",
    FoundInBuild: "Found in build",
    IntegratedInBuild: "Integrated in build",
    WikiPage: "Wiki Page",
    WorkItemLink: "WorkItemLink",
    RemoteWorkItemLink: "RemoteWorkItemLink",
    GitHubPullRequestLinkType: "GitHub Pull Request",
    GitHubCommitLinkType: "GitHub Commit",
    GitHubIssueLinkType: "GitHub Issue",
    ReleaseEnvironment: "Integrated in release environment"
} as const;

export const RegisteredWorkItemLinkTypeNames = {
    Parent: "Parent"
} as const;

export namespace Exceptions {
    export const OperationCanceledException = "VSS.WorkItemTracking.OperationCanceledException";
    export const WorkItemBulkSaveException = "VSS.WorkItemTracking.WorkItemBulkSaveException";
    export const ProjectDoesNotExistException = "VSS.WorkItemTracking.ProjectDoesNotExistException";
    export const FieldDoesNotExistException = "VSS.WorkItemTracking.FieldDoesNotExistException";
    export const LinkTypeEndDoesNotExistException = "VSS.WorkItemTracking.LinkTypeEndDoesNotExistException";
    export const LinkTypeDoesNotExistException = "VSS.WorkItemTracking.LinkTypeDoesNotExistException";
    export const WorkItemSaveFailedDueToInvalidStatusException = "VSS.WorkItemTracking.WorkItemSaveFailedDueToInvalidStatusException";
    export const InvalidQuerySyntaxException = "VSS.WorkItemTracking.InvalidQuerySyntaxException";
    export const InvalidQueryFilterRowException = "VSS.WorkItemTracking.InvalidQueryFilterRowException";
    export const QueryItemAlreadyExistException = "VSS.WorkItemTracking.QueryItemAlreadyExistException";
    export const QueryFolderDoesNotExistException = "VSS.WorkItemTracking.QueryFolderDoesNotExistException";

    export const InvalidOperationException = "System.InvalidOperationException";
}

export enum FieldStatus {
    Valid = 0,
    InvalidEmpty = 1,
    InvalidNotEmpty = 2,
    InvalidFormat = 3,
    InvalidListValue = 4,
    InvalidOldValue = 5,
    InvalidNotOldValue = 6,
    InvalidEmptyOrOldValue = 7,
    InvalidNotEmptyOrOldValue = 8,
    InvalidValueInOtherField = 9,
    InvalidValueNotInOtherField = 10,
    InvalidDate = 11,
    InvalidTooLong = 12,
    InvalidType = 13,
    InvalidComputedField = 14,
    InvalidPath = 15,
    InvalidCharacters = 16,
    InvalidIdentity = 17,
    InvalidUnknown = 18
}

export enum RuleEvaluatorExecutionPhase {
    DefaultRules = 1,
    CopyRules = 2,
    OtherRules = 3
}

export namespace WorkItemCategoryConstants {
    export const HIDDEN = "Microsoft.HiddenCategory";
    export const TEST_CASE = "Microsoft.TestCaseCategory";
    export const TEST_PLAN = "Microsoft.TestPlanCategory";
    export const TEST_SUITE = "Microsoft.TestSuiteCategory";
    export const TEST_SHAREDSTEP = "Microsoft.SharedStepCategory";
    export const TEST_SHAREDPARAMETER = "Microsoft.SharedParameterCategory";
}

export namespace Actions {
    /// <summary>Values to support WIT related actions</summary>
    export const NEW_PROJECT_DATA = "new-project-data";
    export const DISCUSSION_ADDED = "discussion-added";
    export const DISCUSSION_DELETED = "discussion-deleted";
    export const DISCUSSION_UPDATED = "discussion-updated";
    export const RESET_DISCUSSION = "reset-discussion";
    export const RESET_HISTORY = "reset-history";
    export const DISCUSSION_EDIT_DIRTY_CHANGED = "discussion-dirty-changed";
    export const DISCUSSION_COMMENT_UPDATED = "discussion-comment-updated";

    export const WORKITEM_DELETED = "workitem-deleted";
    export const WORKITEM_RESTORED = "workitem-restored";
    export const WORKITEM_DESTROYED = "workitem-destroyed";
    export const WORKITEM_DISCARDED = "workitem-discarded";
    export const WORKITEM_ID_UPDATED = "workitem-id-updated";
    export const WORKITEM_COPIED = "workitem-copied";
}

export const WorkItemWatermarkAllAction = "all-fields";

export interface IDiscussionEditedArguments {
    workItemId: number;
    isCommentDirty: boolean;
}

export namespace RelatedDataKeys {
    /**
     * Helps history control identify if it's outdated prior to being async loaded (e.g. a comment was deleted before switching to History page)
     */
    export const HISTORY_OUTDATED = "history-outdated";
    export const WORKITEM_MANAGER_KEY = "workitem-manager";
}

export interface IDiscussionModifiedEventArguments {
    workItemId: number;
}

export interface IDiscussionCommentUpdatedArguments {
    workItemId: number;
    commentId: number;
}

export namespace WorkItemUpdateResultState {
    export const NoResult = "NoResult";
    export const Error = "Error";
    export const Success = "Success";
}

export namespace BugWITFieldReferenceNames {
    export const ReproSteps = "Microsoft.VSTS.TCM.ReproSteps";
}

export namespace PageSizes {
    export const QUERY = 200;
    export const SAVE = 200;
}

export namespace WorkItemPaneMode {
    export const Off = "off";
    export const Right = "right";
    export const Bottom = "bottom";
}

export namespace LinkTypeName {
    export const Child_Link_Name = "System.LinkTypes.Hierarchy-Forward";
    export const Parent_Link_Name = "System.LinkTypes.Hierarchy-Reverse";
    export const Predecessor_Link_Name = "System.LinkTypes.Dependency-Reverse";
    export const Successor_Link_Name = "System.LinkTypes.Dependency-Forward";
    export const Duplicate_Link_Name = "System.LinkTypes.Duplicate-Forward";
    export const DuplicateOf_Link_Name = "System.LinkTypes.Duplicate-Reverse";
    export const Related_Link_Name = "System.LinkTypes.Related-Forward";
}

export namespace WitFeatureAvailabilityFlags {
    export const DisableWorkItemStoreDataProviders = "WorkItemTracking.Server.DisableWorkItemStoreDataProviders";
    export const DisableSendMailDataProvider = "WorkItemTracking.Server.DisableSendMailDataProvider";
    export const DisableWorkItemColorDataProvider = "WorkItemTracking.Server.DisableWorkItemColorDataProvider";
}

export enum CoreLinkTypes {
    Related = 1,
    Parent = -2,
    Child = 2,
    Predecessor = -3,
    Successor = 3
}

export enum ErrorCodes {
    WorkItemMissingOrUpdated = 600122,
    ResourceLinkNotFoundException = 600180
}

export namespace WorkItemFormPerfConstants {
    // TODO:Replat Change this to WorkItemFormArea once new form is on by default
    export const WorkItemFormArea = "NewWorkItemFormArea";
    export const EditWorkItemView = "EditWorkItemView";
    export const CreateWorkItemView = "CreateWorkItemView";
    export const TakeUpdateResult = "WorkItemTracking.TakeUpdateResult";
    export const EvaluateLinkUpdates = "WorkItemTracking.EvaluateLinkUpdates";
    export const ResetAfterSave = "WorkItemTracking.ResetAfterSave";
}

export class WITCustomerIntelligenceArea {
    public static WORK_ITEM_TRACKING = "WIT";
    public static WORK_ITEM_TRACKING_MOBILE = "WIT_MOBILE";
    public static NEW_QUERIES_EXPERIENCE = "NEW_QUERIES";
    public static WORK_ITEM_TRACKING_NEWNAV = "WIT_NEWNAV";
}

export class WITCustomerIntelligenceFeature {
    public static CLIENTSIDEOPERATION_SEND_EMAIL = "SendEmail";
    public static CLIENTSIDEOPERATION_OPEN_WORK_ITEM_IN_NEW_TAB = "OpenWorkItemInNewTab";
    public static CLIENTSIDEOPERATION_PREFETCH_NEXT_WORKITEM = "PrefetchNextWorkitemInTriageView";
    public static CLIENTSIDEOPERATION_BACK_TO_QUERY_RESULTS: string = "BackToQueryResults";
    public static CLIENTSIDEOPERATION_MAXIMIZE_RICH_EDITOR = "MaximizeRichEditor";
    public static CLIENTSIDEOPERATION_QUERY_SOFTCAP_LINK = "QuerySoftCapLink";
    public static CLIENTSIDEOPERATION_COPY_SELECTION_AS_HTML = "CopySelectionAsHtml";
    public static CLIENTSIDEOPERATION_WORK_ITEM_FINDER = "WorkItemFinder";
    public static CLIENTSIDEOPERATION_QUERYACROSSPROJECTS = "QueryAcrossProjects";
    public static CLIENTSIDEOPERATION_QUERY_WORK_ITEM = "QueryWorkItem";
    public static CLIENTSIDEOPERATION_CREATE_QUERY = "CreateQuery";
    public static CLIENTSIDEOPERATION_CHANGE_QUERY_COLUMNS = "ChangeQueryColumns";
    public static CLIENTSIDEOPERATION_WIT_CLONE = "CloneWorkItem";
    public static CLIENTSIDEOPERATION_QUERY_RESULTS_PROVIDER = "QueryResultsProvider";
    public static CLIENTSIDEOPERATION_COPY_WORKITEM_LINK = "WorkItem.CopyWorkItemLink";
    public static CLIENTSIDEOPERATION_INCLUDE_WORKITEM_LINKS_ATTACHMENTS = "WorkItem.IncludeWorkItemLinksAndAttachments";
    public static CLIENTSIDEOPERATION_COPY_WITH_CHILDREN = "WorkItem.CopyWithChildren";
    public static CLIENTSIDEOPERATION_TOOLBAR_CLICK = "WorkItem.ToolbarButtonClicks";
    public static CLIENTSIDEOPERATION_WIT_TABSELECTION = "WorkItem.TabSelection";
    public static CLIENTSIDEOPERATION_WIT_GROUPCOLLAPSED = "WorkItem.GroupCollapsed";
    public static CLIENTSIDEOPERATION_WIT_SAVING = "WorkItem.Saving";
    public static CLIENTSIDEOPERATION_WIT_CSSNODECACHEINVALIDATED = "CSSNodes.CacheInvalidated";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONOPENED = "WorkItem.DiscussionOpened";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONMESSAGESLOADED = "WorkItem.DiscussionMessagesLoaded";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONCOMMENTSUBMITTED = "WorkItem.DiscussionCommentSubmitted";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONUNSAVEDPREVIEW = "WorkItem.DiscussionUnsavedPreview";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONRETURNTOUNSAVEDCOMMENT = "WorkItem.DiscussionReturnToUnsavedComment";
    public static CLIENTSIDEOPERATION_WIT_MOBILEDISCUSSIONPREVIEWCLICKED = "WorkItem.DiscussionPreviewClicked";
    public static RESULT_FILTER = "ResultFilter";
    public static CLIENTSIDEOPERATION_VS_WORKITEM_NOT_IN_QUERY_RESULTS = "VSOpenInWeb.WorkItemNotInQueryResult";
    public static WORKITEMPANECHANGED = "WorkItemPaneChanged";
    public static WI_FORM_CONTRIBUTION = "FormContribution";
    public static IMPORTCSV_IMPORT = "ImportCsv.Import";
    public static IMPORTCSV_IMPORT_ERROR = "ImportCsv.Import.Error";
    public static ADD_LINK = "AddLink";
    public static GITHUB_ADD_LINK = "GitHubAddLink";
    public static GITHUB_ADD_LINK_ERROR = "GitHubAddLink.Error";

    // Move Work Item
    public static CLIENTSIDEOPERATION_WIT_MOVE_CANCEL = "WorkItemMove.CancelMove";
    public static CLIENTSIDEOPERATION_WIT_MOVE_CANCEL_AFTER_HIDDEN_TYPE_FAILED = "WorkItemMove.CancelAfterHiddenTypeFailed";
    public static CLIENTSIDEOPERATION_WIT_MOVE_CANCEL_AFTER_TYPE_FAILED = "WorkItemMove.CancelAfterTypeFailed";
    public static CLIENTSIDEOPERATION_WIT_MOVE_SUCCEED_AFTER_TYPE_FAILED = "WorkItemMove.PassAfterTypeFailed";
    public static CLIENTSIDEOPERATION_WIT_MOVE_PERMISSIONS_ERROR = "WorkItemMove.PermissionsError";
    public static CLIENTSIDEOPERATION_WIT_MOVE_REVERT = "WorkItemMove.Revert";
    public static CLIENTSIDEOPERATION_WIT_MOVE_SUCCESS = "WorkItemMove.Success";

    // Change Work Item Type
    public static CLIENTSIDEOPERATION_WIT_TYPE_CHANGE_REVERT = "WorkItemChangeType.Revert";
    public static CLIENTSIDEOPERATION_WIT_TYPE_CHANGE_CANCEL_AFTER_FAIL = "WorkItemChangeType.CancelAfterFailed";
    public static CLIENTSIDEOPERATION_WIT_TYPE_CHANGE_CANCEL_AFTER_OPEN = "WorkItemChangeType.CancelAfterOpen";
    public static CLIENTSIDEOPERATION_WIT_TYPE_CHANGE_SUCCESS = "WorkItemChangeType.Success";

    public static BULK_EDIT_TAGS = "BulkEditTags";

    public static TELEMETRY_ASSIGNTO_POPULATEMENU = "AssignTo.PopulateMenu";
    public static TELEMETRY_OPENWORKITEM_CONTEXTMENU = "OpenWorkItemConextMenu";
    public static WORKITEM_DISCUSSIONCONTROL = "WorkItemDiscussion";
    public static WORKITEM_VIEWFOLLOWS = "ViewWorkItemFollows";
    public static WORKITEM_BULKUNFOLLOWS = "BulkUnfollows";

    // Related Work Items Control
    public static CLIENTSIDEOPERATION_RELATEDWORKITEMS_CONTROL_OPENWORKITEM = "RelatedWorkItemsControlOpenWorkItem";

    // New Queries Experience
    public static NEWQUERYEXPERIENCE_NAVIGATE_PIVOT = "NewQueries.NavigatePivot";
    public static NEWQUERYEXPERIENCE_WORKITEMNAVIGATOR = "NewQueries.WorkItemNavigator";
    public static NEWQUERYEXPERIENCE_TRIAGEVIEWHUB_PIVOTCOMMAND = "NewQueries.TriageViewHub.PivotCommand";
    public static NEWQUERYEXPERIENCE_QUERY_ACTION = "NewQueries.QueryAction";
    public static NEWQUERYEXPERIENCE_MOVE_QUERY = "NewQueries.MoveQuery";
    public static NEWQUERYEXPERIENCE_LEAVE_DIRTY = "NewQueries.LeaveDirty";
    public static NEWQUERYEXPERIENCE_ADHOCQUERY_REDIRECTION = "NewQueries.AdhocQuery.Redirection";
    public static NEWQUERYEXPERIENCE_LASTVISITED_REDIRECTION = "NewQueries.LastVisited.Redirection";
    public static NEWQUERYEXPERIENCE_NAVIGATE_QUERIESPIVOT = "NewQueries.Navigate.QueriesPivot";
    public static NEWQUERYEXPERIENCE_BREADCRUMB_CLICKED = "NewQueries.Breadcrumb.Clicked";

    // New Nav
    public static CUSTOMIZE_WORKITEM = "WorkItemToolbar.Customize";

    // Charts
    public static QUERYCHARTS_NAVIGATE = "QueryCharts.Navigate";

    // Queries
    public static QUERY_SEARCH = "Query.Search";

    // WorkItem Field Keyboard shortcut
    public static CLIENTSIDEOPERATION_WIT_FIELDKEYBOARDSHORTCUT = "WorkItemForm.FieldKeyboardShortcut";

    // Classification fields mru error
    public static CLASSIFICATION_MRU_VALUE_CHANGED = "ClassificationFieldsMruValueChanged";

    // WIT Client Side Caching
    public static WIT_CACHE_PROVIDER_INIT = "WorkItemTracking.CacheProvider.Initialize";
    public static WIT_CACHE_PROVIDER_OPEN = "WorkItemTracking.CacheProvider.OpenCache";

    // WIT watchDog service for open WI form
    public static WIT_WATCHDOG_OPEN_FORM = "WorkItemTracking.WatchDog.OpenForm";

    public static LINKSCONTROL_SAMECATEGORY_WARNING = "LinksControl.SameCategory.Warning";

    // WIT filterAllowedFieldValues method usage
    public static FILTER_METHOD_USAGE = "WorkItemTracking.ExtensionFilterMethodUsage";
}

export class WITPerformanceScenario {
    public static WORKITEM_OPEN_NEWFORMDIALOG = "WorkItem.Open.NewFormDialog";
    public static WORKITEM_RELATED_LINKING = "WorkItem.Related.Linking";
    public static WORKITEMFORM_FOLLOWS_ACTION_FOLLOW = "WorkItemForm.Follows.Action.Follow";
    public static WORKITEMFORM_FOLLOWS_ACTION_UNFOLLOW = "WorkItemForm.Follows.Action.Unfollow";
    public static WORKITEM_OPENFORM = "WorkItem.OpenForm";
    public static WORKITEM_OPENFORM_NEWLAYOUT = "WorkItem.OpenForm.NewLayout";
    public static WORKITEM_OPENFORM_CREATE_NEWLAYOUT = "WorkItem.OpenForm.Create.NewLayout";
    public static WORKITEM_OPENDIALOG_NEWLAYOUT = "WorkItem.OpenDialog.NewLayout";
    public static WORKITEM_OPENIDE_NEWLAYOUT = "WorkItem.IDE.OpenForm.NewLayout";
    public static WORKITEM_SAVE = "WorkItem.Save";
    public static WORKITEM_BULKSAVE = "WorkItem.BulkSave";
    public static WORKITEM_CLOSED = "WorkItem.Closed";
    public static WORKITEM_FIELDCHANGED = "WorkItem.FieldChanged";
    public static QUERY_OPENRESULTS = "Query.OpenResults";
    public static QUERY_SAVE = "Query.Save";
    public static QUERY_SAVEAS = "Query.SaveAs";
    public static QUERYHIERARCHY_LOAD = "QueryHierarchy.Load";
    public static HISTORYVIEW_EXTENSION_LOAD = "HistoryView.Extension.Load";
    public static WORKITEM_SIGNALR_REFRESHCOMPLETE = "WorkItem.SignalR.RefreshComplete";
    public static WORKITEM_SIGNALR_REFRESHERROR = "WorkItem.SignalR.RefreshError";
    public static WORKITEM_SIGNALR_ERROR = "WorkItem.SignalR.Error";
    // NQE performance scenarios
    public static QUERIESHUB_TRIAGEVIEW_OPENQUERYRESULTS = "QueriesHub.TriageView.OpenQueryResults";
    public static QUERIESHUB_TRIAGEVIEW_OPENBREADCRUMBQUERYRESULTS = "QueriesHub.TriageView.OpenBreadcrumbQueryResults";
    public static QUERIESHUB_TRIAGEVIEW_OPENWORKITEM = "QueriesHub.TriageView.OpenWorkItem";
    public static QUERIESHUB_TRIAGEVIEW_SAVEEXISTINGQUERY = "QueriesHub.TriageView.SaveExistingQuery";
    public static QUERIESHUB_QUERIESVIEW_OPENFAVORITESPIVOT = "QueriesHub.QueriesView.OpenFavoritesPivot";
    public static QUERIESHUB_QUERIESVIEW_OPENALLQUERIESPIVOT = "QueriesHub.QueriesView.OpenAllQueriesPivot";
}
